import React, { useState } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCopy,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { formattedAmount } from "../../../utils/helpers/Functions.helper";

const UserReferral = ({ handleCloseOrderModal, isOrderDetail, itemData }) => {
  const [copied, setCopied] = useState(false);
  const {
    email = "",
    first_name = "",
    last_name = "",
    profile_image = "",
    phone_number = "",
    country,
    referral_code = "N/A",
    average_rating = "N/A",
    status = "Inactive",
    is_active = false,
    redeem_points = 0,
    referral_count = 0,
    referral_amount = 0,
    createdAt,
  } = itemData || {};

  const { theme } = useSelector((state) => state.theme);

  const handleCopy = () => {
    navigator.clipboard.writeText(referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!itemData) {
    return null; // or a loader/skeleton
  }

  const fullName = `${first_name} ${last_name}`.trim();
  const initials = first_name.charAt(0).toUpperCase();

  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 md:p-8 max-w-2xl mx-auto border border-gray-100">
      {/* User Header */}
      <div className="flex items-center space-x-5 mb-8">
        {/* Profile Avatar */}
        <div className="relative">
          {profile_image ? (
            <img
              src={profile_image}
              alt={`${fullName}'s profile`}
              className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover shadow-lg border-4 border-white"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = ""; // Trigger fallback
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          {/* Fallback Initials */}
          <div
            className={`${
              profile_image ? "hidden" : "flex"
            } w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-pink-400 to-red-500 rounded-full items-center justify-center text-2xl md:text-3xl font-bold text-white shadow-lg`}
          >
            {initials}
          </div>
        </div>

        <div className="flex-1">
          <h2 className="text-2xl md:text-3xl font-bold capitalize text-gray-900">
            {fullName || "User"}
          </h2>
          <p
            className={`text-sm md:text-base font-medium mt-1 ${
              is_active ? "text-green-600" : "text-red-600"
            }`}
          >
            ● {status}
          </p>
        </div>
      </div>

      {/* Contact Info */}
      <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-2xl p-6 mb-8 shadow-inner">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h3>

        <div className="space-y-4">
          <div className="flex items-center">
            <FaEnvelope className="mr-3 text-pink-600 text-lg" />
            <div>
              <p className="text-sm font-medium text-gray-700">Email</p>
              <p className="text-gray-600">{email || "N/A"}</p>
            </div>
          </div>

          <div className="flex items-center">
            <FaPhoneAlt className="mr-3 text-pink-600 text-lg" />
            <div>
              <p className="text-sm font-medium text-gray-700">Phone</p>
              <p className="text-gray-600">{phone_number || "N/A"}</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaMapMarkerAlt className="mr-3 text-pink-600 text-lg" />
              <div>
                <p className="text-sm font-medium text-gray-700">Location</p>
                <p className="text-gray-600">{country?.name || "N/A"}</p>
              </div>
            </div>
            {country?.flag && (
              <img
                src={country.flag}
                alt={`${country.name} flag`}
                className="w-8 h-6 rounded-sm shadow-sm object-cover"
              />
            )}
          </div>
        </div>
      </div>

      {/* Referral Code */}
      <div className="bg-gradient-to-r from-pink-500 to-red-600 p-5 rounded-2xl shadow-lg text-white flex flex-col sm:flex-row justify-between items-center mb-8">
        <div className="text-center sm:text-left mb-3 sm:mb-0">
          <h3 className="text-sm font-medium opacity-90">Referral Code</h3>
          <p className="text-2xl md:text-3xl font-bold mt-1 tracking-wider">{referral_code}</p>
        </div>
        <button
          onClick={handleCopy}
          className="bg-white text-pink-600 px-5 py-2.5 rounded-xl font-medium text-sm flex items-center space-x-2 hover:bg-gray-100 transition-all shadow-md"
        >
          <FaCopy className="text-lg" />
          <span>{copied ? "Copied!" : "Copy Code"}</span>
        </button>
      </div>

      {/* Referral Stats Grid */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Referral Stats</h3>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Referrals", value: referral_count },
            { label: "Earnings", value: formattedAmount(referral_amount) },
            { label: "Redeem Points", value: redeem_points },
            { label: "Avg Rating", value: average_rating },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="bg-gradient-to-r from-pink-50 to-red-50 p-4 rounded-xl text-center shadow-sm"
            >
              <p className="text-2xl font-bold text-pink-600">{stat.value}</p>
              <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Joined Date */}
      <div className="text-center text-sm text-gray-500">
        Joined on {createdAt ? new Date(createdAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }) : "N/A"}
      </div>
    </div>
  );
};

export default UserReferral;